import { useCallback, useEffect, useMemo, useState } from "react";
import type { NotificationItem } from "../components/ui/NotificationBell";
import { useAuth } from "../context/AuthContext";
import { registerWebPush } from "../lib/webPush";

type UseWebPushNotificationsParams = {
  storageKey: string;
};

const readStoredNotifications = (
  storageKey: string
): NotificationItem[] => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveStoredNotifications = (
  storageKey: string,
  notifications: NotificationItem[]
) => {
  window.localStorage.setItem(
    storageKey,
    JSON.stringify(notifications.slice(0, 50))
  );
};

const normalizePushPayload = (payload: any): NotificationItem => {
  const notification =
    payload?.notification || payload?.message || payload || {};

  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: notification.title || "Lynx",
    body: notification.body || "Nueva notificación",
    data: notification.data || {},
    receivedAt: notification.receivedAt || new Date().toISOString(),
    read: false,
  };
};

export const useWebPushNotifications = ({
  storageKey,
}: UseWebPushNotificationsParams) => {
  const { token, userId } = useAuth();

  const [notifications, setNotifications] = useState<NotificationItem[]>(
    () => readStoredNotifications(storageKey)
  );

  const unreadCount = useMemo(
    () =>
      notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const addNotification = useCallback(
    (notification: NotificationItem) => {
      setNotifications((previous) => {
        const next = [notification, ...previous].slice(0, 50);
        saveStoredNotifications(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const markAsRead = useCallback(() => {
    setNotifications((previous) => {
      const next = previous.map((notification) => ({
        ...notification,
        read: true,
      }));

      saveStoredNotifications(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    saveStoredNotifications(storageKey, []);
  }, [storageKey]);

  useEffect(() => {
    registerWebPush({ token, userId }).catch((error) => {
      console.error(
        "[Lynx Push] Error inicializando Web Push",
        error
      );
    });
  }, [token, userId]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type !== "LYNX_PUSH_RECEIVED") return;

      addNotification(normalizePushPayload(event.data.payload));
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
    };
  }, [addNotification]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    clearNotifications,
  };
};